import React, { useEffect, useMemo, useState } from "react";
import CrudModule from "@/components/admin/CrudModule";
import { eventsCrud } from "@/services/eventsService";
import { unitsCrud } from "@/services/unitsService";
import { storageService } from "@/services/storageService";
import { formatDateShort } from "@/lib/format";

const FORUM_OPTIONS = ["socio-civic-political", "environment", "youth-training"];

export default function AdminEventsPage() {
  const [units, setUnits] = useState([]);

  useEffect(() => {
    unitsCrud
      .list()
      .then((rows) => {
        setUnits(
          rows.sort(
            (a, b) =>
              (a.sort_order || 0) - (b.sort_order || 0) ||
              (a.name || "").localeCompare(b.name || "")
          )
        );
      })
      .catch(() => setUnits([]));
  }, []);

  const unitOptions = useMemo(
    () => units.map((u) => ({ value: u.slug, label: u.name || u.slug })),
    [units]
  );

  const fields = useMemo(
    () => [
      { name: "title", label: "Title", type: "text", required: true },
      { name: "slug", label: "Slug", type: "text", required: true, hint: "URL identifier, e.g. sabha-day-2026" },
      { name: "event_date", label: "Date", type: "date", required: true },
      { name: "event_time", label: "Time", type: "text", placeholder: "e.g. 4:00 PM onwards" },
      { name: "venue", label: "Venue", type: "text", required: true },
      {
        name: "cover_image",
        label: "Cover Image",
        type: "image",
        onUpload: (file, hint) => storageService.uploadEventGalleryImage(file, hint),
        folderLabel: "event_gallery",
        nameHintFrom: "slug",
      },
      { name: "forum_slug", label: "Linked Forum (optional)", type: "select", options: FORUM_OPTIONS },
      {
        name: "unit_slug",
        label: "Linked Unit (optional)",
        type: "select",
        options: unitOptions,
        hint: "Select a parish unit to link this event to its unit page.",
      },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "gallery", label: "Gallery", type: "gallery" },
      { name: "documents", label: "Documents (JSON array of {title, file_url})", type: "json" },
      { name: "is_published", label: "Published", type: "checkbox", default: true },
      { name: "is_featured", label: "Featured", type: "checkbox", default: false },
    ],
    [unitOptions]
  );

  return (
    <CrudModule
      title="Events"
      description="Upcoming and past events, with venue, imagery, galleries and downloads."
      crud={eventsCrud}
      searchKeys={["title", "venue", "forum_slug", "unit_slug"]}
      columns={[
        { key: "title", label: "Event" },
        { key: "event_date", label: "Date", render: (r) => formatDateShort(r.event_date) },
        { key: "venue", label: "Venue" },
        { key: "forum_slug", label: "Forum / Unit", render: (r) => r.forum_slug || r.unit_slug || "—" },
      ]}
      toggles={[
        { key: "is_published", label: "Published" },
        { key: "is_featured", label: "Featured" },
      ]}
      fields={fields}
    />
  );
}
